import { Plus, Warehouse as WarehouseIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import useSWR from 'swr'

import { EmptyState } from '@/components/EmptyState'
import { PageHeader } from '@/components/ds'
import { Button } from '@/components/ui/button'
import { WarehouseStats } from '@/containers/Warehouses/WarehouseItem'
import { Warehouses } from '@/containers/Warehouses/Warehouses'
import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import { apiFetcher } from '@/data/fetcher'
import { WarehouseFormModal } from '@/forms/WarehouseForm/WarehouseFormModal'
import { StockItem } from '@/interfaces/warehouse/StockItem'
import { Warehouse } from '@/interfaces/warehouse/Warehouse'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'
import { formatCount } from '@/lib/catalog/catalogTypes'
import { stockLevelOf } from '@/lib/warehouse/stockLevels'

export default function WarehousesPage() {
  const { t } = useTranslation(undefined, { keyPrefix: 'warehouse' })

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingWarehouse, setEditingWarehouse] = useState<Warehouse | undefined>()

  const { data: warehousesResponse, isLoading, mutate } = useSWR<{ data?: Warehouse[]; total?: number }>(
    '/api/v2/warehouses',
    apiFetcher,
  )
  const { data: stockResponse } = useSWR<{ data?: StockItem[]; total?: number }>('/api/v2/stock-items', apiFetcher)

  const warehouses = warehousesResponse?.data || []

  const statsByWarehouseId = useMemo(() => {
    const map = new Map<string, WarehouseStats>()
    for (const item of stockResponse?.data ?? []) {
      const stats = map.get(item.warehouseId) ?? { items: 0, onHand: 0, reserved: 0, low: 0, out: 0 }
      stats.items += 1
      const level = stockLevelOf(item)
      if (level !== 'unlimited') {
        stats.onHand += item.quantityOnHand
        stats.reserved += item.quantityReserved
      }
      if (level === 'low') stats.low += 1
      if (level === 'out') stats.out += 1
      map.set(item.warehouseId, stats)
    }
    return map
  }, [stockResponse])

  useSetPageHeader({
    breadcrumbs: [
      { href: '/warehouse/warehouses', children: t('title') },
      { href: '/warehouse/warehouses', children: t('warehouses.title') },
    ],
  })

  const handleCreate = () => {
    setEditingWarehouse(undefined)
    setIsModalOpen(true)
  }

  const handleDelete = async (warehouse: Warehouse) => {
    try {
      await API.delete(`/api/v2/warehouses/${warehouse.id}`)
      toast.success(t('warehouses.delete.success'))
      mutate()
    } catch (error: unknown) {
      console.error('Delete error:', error)
      toast.error(t('warehouses.delete.error'), {
        description: extractErrorMessage(error) || t('warehouses.error.description'),
      })
      throw error
    }
  }

  return (
    <>
      <title>{t('warehouses.title')}</title>
      <div className="mx-auto w-full max-w-[1280px]">
        <PageHeader
          title={t('warehouses.title')}
          description={isLoading ? undefined : formatCount(warehouses.length, 'sklad', 'sklady', 'skladů')}
          actions={
            <Button onClick={handleCreate}>
              <Plus />
              {t('warehouses.actions.create')}
            </Button>
          }
        />

        {!isLoading && warehouses.length === 0 ? (
          <EmptyState icon={WarehouseIcon}
            title="Zatím nemáte žádný sklad"
            description="Založte sklad a začněte sledovat zásoby produktů."
            ctaButton={<Button onClick={handleCreate}>{t('warehouses.actions.create')}</Button>}
          />
        ) : (
          <Warehouses
            warehouses={warehouses}
            statsByWarehouseId={stockResponse ? statsByWarehouseId : undefined}
            isLoading={isLoading}
            onEdit={(warehouse) => {
              setEditingWarehouse(warehouse)
              setIsModalOpen(true)
            }}
            onDelete={handleDelete}
          />
        )}
      </div>

      <WarehouseFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setEditingWarehouse(undefined)
        }}
        warehouse={editingWarehouse}
        onSuccess={() => mutate()}
      />
    </>
  )
}
